/**
 * 사용자가 직접 수정한 셀(changeOrigin === "manual")의 기존 값/수정한 값을
 * 계산하는 훅. IxdPreviewRenderer 의 `getModifiedValues` 로 넘겨져, 셀 옆
 * 연동(linkage) popover 하단(연동이 없으면 단독 '수정' 패널)에 표시된다.
 *
 * '기존 값' 규칙: 값 반영을 누르지 않았으면 처음(서버) 값, 값 반영을 눌렀으면
 * 반영으로 들어간 값. undo 스택에서 해당 키의 previousOrigin이 "manual"이 아닌
 * 가장 최근 항목의 previousValue가 정확히 이 값이다.
 */

import { useCallback } from "react";

import type { FsType, IxdChunk } from "@/apis/ixd-editor/types";
import type { ModifiedCellDisplayValues } from "@/components/ixd-preview";
import {
  type CellChangeOrigin,
  type CellIdentityLocation,
  makeChangeKey,
} from "@/utils/ixd-cell-model";
import {
  useEditSessionStore,
  type EditSessionState,
} from "@/hooks/ixd-edit-session";
// barrel(@/hooks/ixd-edit-session) 대신 deep import — barrel이 api/firebase까지
// 끌고 와 이 파일을 import하는 테스트가 env 없이는 못 돈다
import { ATTR } from "@/utils/dom-attrs";
import { formatCellForDisplay } from "@/utils/ixd-editor/cell-display";
import { getUnitDivisor } from "@/utils/ixd-editor/unit-conversion";
import { findTopmostChangeLocation } from "./undo-focus";

/**
 * '기존 값'(raw)을 undo 스택에서 찾는다. 최근 그룹부터 거슬러 올라가며 이 키의
 * previousOrigin이 "manual"이 아닌 첫 항목의 previousValue를 반환한다.
 * undefined면 편집 전(= 처음 값) — 호출부가 location.value로 대체한다.
 */
export const findOriginalRawValue = (
  undoStack: EditSessionState["undoStack"],
  fsType: FsType,
  changeKey: string,
): string | undefined => {
  for (let i = undoStack.length - 1; i >= 0; i -= 1) {
    for (const entry of undoStack[i]) {
      if (entry.fsType !== fsType || entry.changeKey !== changeKey) continue;
      if (entry.previousOrigin !== "manual") return entry.previousValue;
    }
  }
  return undefined;
};

const getCellUnitDivisor = (cell: HTMLElement): number | undefined => {
  const unitDivisor = Number(cell.dataset.unitDivisor);
  return Number.isFinite(unitDivisor) && unitDivisor > 0
    ? unitDivisor
    : undefined;
};

/** 대상 셀과 동일한 규칙으로 raw 값을 표시 문자열로 변환한다. */
const formatRawValueLikeCell = (
  rawValue: string,
  cell: HTMLElement,
  unitDivisor: number | undefined = getCellUnitDivisor(cell),
): string => {
  const decimalDigits = cell.dataset.decimalDigits;
  return formatCellForDisplay({
    rawText: rawValue,
    schemaType: cell.dataset.schemaType,
    unitDivisor,
    negated: cell.hasAttribute("data-negated"),
    decimalDigits:
      decimalDigits !== undefined && decimalDigits !== ""
        ? Number(decimalDigits)
        : null,
  }).displayText;
};

/**
 * `IxdPreviewRenderer.getModifiedValues` 용 콜백을 만든다. 수정 셀이 아니면
 * null. 세션 상태가 바뀔 때마다 콜백 identity 도 바뀌어 renderer 가 popover 를
 * 재평가한다.
 */
export const useGetModifiedCellValues = ({
  chunks,
  fsType,
  cellChanges,
  changeOrigins,
  identityLocationsByKey,
  identityKeyByNormalizedCellId,
}: {
  chunks: IxdChunk[];
  fsType: FsType;
  cellChanges: Record<string, string>;
  changeOrigins: Record<string, CellChangeOrigin>;
  identityLocationsByKey: ReadonlyMap<string, readonly CellIdentityLocation[]>;
  identityKeyByNormalizedCellId: ReadonlyMap<string, string>;
}) => {
  const undoStack = useEditSessionStore((s) => s.undoStack);

  return useCallback(
    (cell: HTMLElement): ModifiedCellDisplayValues | null => {
      const cellId = cell.getAttribute(ATTR.cellId);
      if (!cellId) return null;

      const changeKey = makeChangeKey(
        cell.getAttribute(ATTR.cellIdentityKey),
        cellId,
      );
      if (changeOrigins[changeKey] !== "manual") return null;

      const originalRawValue =
        findOriginalRawValue(undoStack, fsType, changeKey) ??
        findTopmostChangeLocation([{ fsType, changeKey }], {
          chunks,
          identityLocationsByKey,
          identityKeyByNormalizedCellId,
        })?.value;
      if (originalRawValue === undefined) return null;

      const modifiedRawValue = cellChanges[changeKey] ?? "";

      // 연동 표들의 최저 단위(기준 단위). 셀 단위가 더 크면 수정이 반올림에
      // 묻힐 수 있어(예: 12345→12355는 천원 표기로 동일) 환산값을 함께 준다.
      // ponytail: min에 자기 위치도 포함 — 자기 단위가 최저면 조건이 성립하지
      // 않아 제외 계산과 결과가 같다.
      const identityKey = cell.getAttribute(ATTR.cellIdentityKey);
      const locations = identityKey
        ? identityLocationsByKey.get(identityKey)
        : undefined;
      const cellDivisor = getCellUnitDivisor(cell) ?? 1;
      const baseDivisor = locations?.length
        ? Math.min(...locations.map((l) => getUnitDivisor(l.unitText)))
        : cellDivisor;

      return {
        original: formatRawValueLikeCell(originalRawValue, cell),
        modified: formatRawValueLikeCell(modifiedRawValue, cell),
        baseUnit:
          baseDivisor < cellDivisor
            ? {
                original: formatRawValueLikeCell(
                  originalRawValue,
                  cell,
                  baseDivisor,
                ),
                modified: formatRawValueLikeCell(
                  modifiedRawValue,
                  cell,
                  baseDivisor,
                ),
              }
            : null,
      };
    },
    [
      cellChanges,
      changeOrigins,
      chunks,
      fsType,
      identityKeyByNormalizedCellId,
      identityLocationsByKey,
      undoStack,
    ],
  );
};
